import React, { useEffect, useMemo, useRef, useState } from "react";
import CampaignPickerModal from "../components/CampaignPickerModal";

const TIKTOK_API_BASE = (import.meta.env.VITE_TIKTOK_API_BASE || "").replace(/\/+$/, "");

export default function TikTokSearch() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [qInput, setQInput] = useState("");
  const [days, setDays] = useState(30);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [enrichedData, setEnrichedData] = useState({});
  const [revealing, setRevealing] = useState(new Set());

  const [pickerOpen, setPickerOpen] = useState(false);
  const [sortBy, setSortBy] = useState("views");
  const [sortDir, setSortDir] = useState("desc");

  const onSearch = async () => {
    if (!qInput.trim()) return;
    setLoading(true);
    setSelectedIds(new Set());
    setData([]);
    try {
      const res = await fetch(`${TIKTOK_API_BASE}/api/tiktok/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keyword: qInput, timeWindowDays: days })
      });
      const json = await res.json();
      setData(json.items || []);
    } catch (e) {
      alert("Search failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const onReveal = async (userKey, e) => {
    e?.stopPropagation();

    // Only bail out if we *already* have followers, not just any object
    if (enrichedData[userKey]?.followers != null) return;

    setRevealing(prev => new Set(prev).add(userKey));
    try {
      const res = await fetch(`${TIKTOK_API_BASE}/api/tiktok/enrich`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: userKey })
      });

      if (!res.ok) {
        // Try to extract server error message
        let msg = `Enrich failed (${res.status})`;
        try {
          const errJson = await res.json();
          if (errJson?.error) msg = errJson.error;
        } catch (_) { }
        throw new Error(msg);
      }

      const json = await res.json();

      // Optional: sanity check
      if (json.followers == null) {
        throw new Error("No followers field returned from enrich API");
      }

      setEnrichedData(prev => ({ ...prev, [userKey]: json }));
    } catch (e) {
      console.error(e);
      alert("Could not reveal stats: " + e.message);
    } finally {
      setRevealing(prev => {
        const n = new Set(prev);
        n.delete(userKey);
        return n;
      });
    }
  };


  const getRow = (item) => {
    const extra = enrichedData[item.id] || {};
    return {
      ...item,
      followers: extra.followers ?? item.followers,
      totalLikes: extra.totalLikes ?? item.totalLikes,
      contact: extra.contact || item.contact || {}
    };
  };

  const sorted = useMemo(() => {
    const rows = data.map(getRow);
    return rows.sort((a, b) => {
      let valA, valB;
      if (sortBy === 'rank') { valA = a.ranking.topVideoRank; valB = b.ranking.topVideoRank; }
      if (sortBy === 'views') { valA = a.topVideo.views; valB = b.topVideo.views; }
      if (sortBy === 'followers') { valA = a.followers || 0; valB = b.followers || 0; }
      if (sortBy === 'date') { valA = new Date(a.lastPostDate); valB = new Date(b.lastPostDate); }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, enrichedData, sortBy, sortDir]);

  const getSelectedForCampaign = () => {
    return data
      .filter(r => selectedIds.has(r.id))
      .map(getRow)
      .filter(r => r.contact?.email)
      .map(r => ({
        email: r.contact.email,
        curator_name: r.name,
        meta: {
          username: r.username,
          profile: `https://tiktok.com/${r.username}`,
          followers: r.followers
        }
      }));
  };

  return (
    <div className="psl-shell" style={{ padding: 20, overflow: 'auto' }}>

      <div style={{ marginBottom: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input
          className="psl-input"
          style={{ maxWidth: 300 }}
          placeholder="Search Keyword..."
          value={qInput}
          onChange={e => setQInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onSearch()}
        />
        <select className="psl-input" style={{ width: 'auto' }} value={days} onChange={e => setDays(e.target.value)}>
          <option value="7">Last 7 Days</option>
          <option value="30">Last 30 Days</option>
          <option value="60">Last 60 Days</option>
          <option value="90">Last 90 Days</option>
        </select>
        <button className="psl-btn primary" onClick={onSearch} disabled={loading}>
          {loading ? "Scanning TikTok..." : "Find Influencers"}
        </button>
      </div>

      <div className="psl-toolbar">
        <div className="psl-count">Found: {sorted.length} influencers (Strict Filter)</div>
        <div className="psl-actions-row">
          <button className="psl-btn" disabled={selectedIds.size === 0} onClick={() => setPickerOpen(true)}>
            Add Selected to Campaign
          </button>
        </div>
      </div>

      <div className="psl-tableblock">
        <table className="psl-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}><input type="checkbox" /></th>
              <th onClick={() => setSortBy('rank')} style={{ cursor: 'pointer' }}>Rank</th>
              <th>Influencer</th>
              <th onClick={() => setSortBy('followers')} style={{ cursor: 'pointer' }}>Profile Stats</th>
              <th>Top Video</th>
              <th onClick={() => setSortBy('views')} style={{ cursor: 'pointer' }}>Video Stats</th>
              <th>Contact Info (Verified)</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(row => {
              const isRevealing = revealing.has(row.id);
              const { email, ig, twitter, youtube, discord } = row.contact;

              return (
                <tr key={row.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => setSelectedIds(prev => {
                        const n = new Set(prev);
                        n.has(row.id) ? n.delete(row.id) : n.add(row.id);
                        return n;
                      })}
                    />
                  </td>
                  <td>#{row.ranking.topVideoRank}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <img
                        // 1. Route through weserv.nl proxy to bypass TikTok hotlink protection
                        src={`https://images.weserv.nl/?url=${encodeURIComponent(row.avatarUrl)}&w=64&h=64&fit=cover`}
                        alt={row.name}
                        style={{ width: 32, height: 32, borderRadius: '50%', background: '#eee', objectFit: 'cover' }}
                        // 2. Fallback to initials if the proxy fails
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(row.name)}&background=random`;
                        }}
                      />
                      <div>
                        <div style={{ fontWeight: 700 }}>{row.name}</div>
                        <a href={`https://tiktok.com/${row.username}`} target="_blank" rel="noreferrer" className="psl-url" style={{ fontSize: 12 }}>
                          {row.username}
                        </a>
                      </div>
                    </div>
                  </td>
                  <td>
                    {row.followers !== null ? (
                      <div style={{ fontSize: 12 }}>
                        <div><b>{row.followers?.toLocaleString()}</b> followers</div>
                        <div style={{ color: '#666' }}>{row.totalLikes?.toLocaleString()} likes</div>
                      </div>
                    ) : (
                      <button
                        className="psl-btn psl-btn--micro"
                        onClick={(e) => onReveal(row.id, e)}
                        disabled={isRevealing}
                        style={{ opacity: 0.7 }}
                      >
                        {isRevealing ? "Loading..." : "Reveal Stats"}
                      </button>
                    )}
                  </td>
                  <td>
                    <a href={row.topVideo.url} target="_blank" rel="noreferrer" className="psl-url" style={{ fontSize: 12 }}>Watch Video ↗</a>
                  </td>
                  <td>
                    <div style={{ fontSize: 12 }}>
                      <div>{row.topVideo.views.toLocaleString()} views</div>
                      <div style={{ color: '#666' }}>{row.topVideo.likes.toLocaleString()} likes</div>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {email && <a href={`mailto:${email}`} style={{ fontWeight: 'bold', color: '#2563eb' }}>{email}</a>}
                      {ig && <div style={{ color: '#d62976', fontSize: 12 }}>IG: {ig}</div>}
                      {twitter && <div style={{ color: '#1da1f2', fontSize: 12 }}>X: {twitter}</div>}
                      {youtube && <div style={{ color: '#ff0000', fontSize: 12 }}>YT: {youtube}</div>}
                      {discord && <div style={{ color: '#5865F2', fontSize: 12 }}>DC: {discord}</div>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pickerOpen && (
        <CampaignPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          selectedRows={getSelectedForCampaign()}
          apiFetch={(url, opts) => fetch(TIKTOK_API_BASE + url, opts).then(r => r.json())}
          onAdded={() => { alert("Added to campaign!"); setPickerOpen(false); }}
        />
      )}
    </div>
  );
}
